import React, { useEffect, useRef, useState } from "react";
import { CsrAPI } from "../../apis/CsrAPI";
import DashboardContent from "../../components/DashboardContent";
import { PERMISSIONS } from "../../utils/permissions";
import { FieldGroup, FormArray, FormBuilder } from "react-reactive-form";
import BioavailabilityQuestions from "../../questions/BioavailabilityQuestions";
import useQuestions from "../../hooks/useQuestions";
import useCsr from "../../hooks/useCsr";
import { toast, ToastContainer } from "react-toastify";
import "react-toastify/dist/ReactToastify.css";
import * as Sentry from "@sentry/react";
import { useParams } from "react-router-dom";
import { populateValueHelperArray } from "./utils/saveRestoreUtils";
import { createUnifiedSubmitHandler } from "../../utils/unifiedFormSubmit";
import { WIZARD_SECTIONS } from "../../utils/wizardSections";
import {
  getRestorableFormArray,
  getRestoreArrayType,
  toRestorableArray,
} from "../../utils/wizardFormRestore";

const isProduction = process.env.NODE_ENV === "production";

const Bioavailability = () => {
  const { studyId } = useParams();
  const { setAjaxLoading } = useCsr();
  const {
    questionsData,
    questionsDataSaved,
    setQuestionsDataSaved,
    loadWizardSections,
    qForm,
    wizardForm,
    setFormFilledData,
    setWizardFormFilledData,
  } = useQuestions();

  const formArraysKeys = {
    qEfficacyAnalytes: "object",
    qPrimaryEfficacyComparisonNames: "object",
    qEfficacyPopulations: "object",
    qEfficacyAnalysisTypeNames: "object",
    qEfficacyTimepointNames: "object",
    qEfficacyKaplanMeierAnalytes: "object",
    qTgNamesDoseFinding: "object",
    qTgNames: "object",
    qTgNamesComparison: "object",
    qPrimaryEfficacyAnalytes: "object",
    qPrimaryEfficacyPopulation: "object",
    qPrimaryEfficacyAnalysisTypeNames: "object",
    qPrimaryEfficacyAnalytesComparison: "object",
    qPrimaryEfficacyBaseline: "object",
    qPrimaryEfficacyTimepointNames: "object",
    qSecondaryEfficacyAnalytes: "object",
    qSubgroupClassification: "object",
    qPkStdColumn: "object",
    qPkTimelines: "object",
    qPkAnalytes: "object",
  };

  const errRef = useRef();
  const [errMsg, setErrMsg] = useState("");
  const [isSaving, setIsSaving] = useState(false);
  const [, setRestoreVersion] = useState(0);
  const lastRestoredQuestionsDataRef = useRef(null);

  useEffect(() => {
    setAjaxLoading(true);
    loadWizardSections([WIZARD_SECTIONS.bioavailability])
      .then(() => {
        setErrMsg("");
      })
      .catch(error => {
        if (isProduction) {
          Sentry.captureException(error);
        }
        if (!error?.response) {
          setErrMsg("No Server Response");
        } else if (error.response?.status === 401) {
          setErrMsg("Invalid user credentials");
        } else {
          setErrMsg("Failed to get questions");
        }
        errRef.current?.focus();
      })
      .finally(() => {
        setAjaxLoading(false);
      });
  }, [studyId, loadWizardSections, setAjaxLoading]);

  useEffect(() => {
    if (!isSaving) {
      retrieveSavedWizardState();
    }
  }, [questionsDataSaved, isSaving]);

  const retrieveSavedWizardState = () => {
    if (isSaving) {
      return;
    }

    if (questionsDataSaved && questionsDataSaved.wizardForm) {
      const wizardFormValue = questionsDataSaved.wizardForm;

      if (lastRestoredQuestionsDataRef.current === questionsDataSaved) {
        return;
      }
      lastRestoredQuestionsDataRef.current = questionsDataSaved;

      // Clear existing form arrays before repopulating
      Object.keys(wizardFormValue).forEach(key => {
        if (
          !getRestoreArrayType(
            wizardForm,
            key,
            formArraysKeys,
            wizardFormValue[key]
          )
        ) {
          return;
        }

        const control = getRestorableFormArray(wizardForm, key);
        while (control && control.length > 0) {
          control.removeAt(0);
        }
      });

      Object.keys(wizardFormValue).map(function (key) {
        const arrayType = getRestoreArrayType(
          wizardForm,
          key,
          formArraysKeys,
          wizardFormValue[key]
        );

        if (arrayType) {
          const control = getRestorableFormArray(wizardForm, key);
          const items = toRestorableArray(wizardFormValue[key], arrayType);

          items.forEach(item => {
            control?.push(
              arrayType === "object"
                ? FormBuilder.group(item)
                : FormBuilder.control(item)
            );
          });
        } else {
          if (!wizardForm.contains(key)) {
            wizardForm.addControl(
              key,
              FormBuilder.control(wizardFormValue[key])
            );
          } else {
            wizardForm.patchValue({ [key]: wizardFormValue[key] });
          }
        }
        return true;
      });

      // Ensure qPK control exists
      if (!wizardForm.contains("qPK")) {
        wizardForm.addControl(
          "qPK",
          FormBuilder.control(wizardFormValue.qPK || null)
        );
      }

      // Restore qPkRelBio root-level control
      if (
        wizardFormValue.qPkRelBio !== undefined &&
        wizardFormValue.qPkRelBio !== null
      ) {
        if (!wizardForm.contains("qPkRelBio")) {
          wizardForm.addControl(
            "qPkRelBio",
            FormBuilder.control(
              wizardFormValue.qPkRelBio === "true" ||
                wizardFormValue.qPkRelBio === true
                ? "true"
                : "false"
            )
          );
        } else {
          wizardForm
            .get("qPkRelBio")
            .setValue(
              wizardFormValue.qPkRelBio === "true" ||
                wizardFormValue.qPkRelBio === true
                ? "true"
                : "false"
            );
        }
      }

      // Restore qPkRelBioAnalyses blocks to individual form controls
      if (
        wizardFormValue.qPkRelBioAnalyses &&
        Array.isArray(wizardFormValue.qPkRelBioAnalyses)
      ) {
        wizardFormValue.qPkRelBioAnalyses.forEach((block, index) => {
          const suffix = index > 0 ? `_${index + 1}` : "";

          if (
            block.qPkRelBioTable !== undefined &&
            block.qPkRelBioTable !== null
          ) {
            const controlName = `qPkRelBioTable${suffix}`;
            if (!wizardForm.contains(controlName)) {
              wizardForm.addControl(controlName, FormBuilder.control(""));
            }
            const tableValue = Array.isArray(block.qPkRelBioTable)
              ? (block.qPkRelBioTable[0] ?? "")
              : block.qPkRelBioTable;
            wizardForm.get(controlName).setValue(tableValue);
          }

          const comparisonPairControlName = `qPkRelBioComparisonPair${suffix}`;
          if (
            block.qPkRelBioComparisonPair &&
            typeof block.qPkRelBioComparisonPair === "object"
          ) {
            // Saved as an array ([{Test, Reference}]) or plain object – handle both
            const pair = Array.isArray(block.qPkRelBioComparisonPair)
              ? block.qPkRelBioComparisonPair[0] || {}
              : block.qPkRelBioComparisonPair;
            const testVal = pair.Test || "";
            const refVal = pair.Reference || "";
            // Always push a new group with a fresh meta.key so React remounts
            // the FieldControl and picks up the restored values (Q1b fix).
            const newPairGroup = FormBuilder.group({
              Test: [testVal],
              Reference: [refVal],
            });
            newPairGroup.meta = { key: `relbio_pair_${index}_${Date.now()}` };
            if (!wizardForm.contains(comparisonPairControlName)) {
              wizardForm.addControl(
                comparisonPairControlName,
                new FormArray([newPairGroup])
              );
            } else {
              const arr = wizardForm.get(comparisonPairControlName);
              while (arr.length > 0) {
                arr.removeAt(0);
              }
              arr.push(newPairGroup);
            }
          }

          const timelinesControlName = `qPkRelBioTimelines${suffix}`;
          populateValueHelperArray(
            wizardForm,
            timelinesControlName,
            block.qPkRelBioTimelines
          );

          const estimateRangeControlName = `qPkRelBioEstimateRange${suffix}`;
          if (
            block.qPkRelBioEstimateRange &&
            typeof block.qPkRelBioEstimateRange === "object"
          ) {
            // Saved as an array ([{min, max}]) or plain object – handle both
            const er = Array.isArray(block.qPkRelBioEstimateRange)
              ? block.qPkRelBioEstimateRange[0] || {}
              : block.qPkRelBioEstimateRange;
            const minVal =
              er.min !== undefined && er.min !== null ? String(er.min) : "";
            const maxVal =
              er.max !== undefined && er.max !== null ? String(er.max) : "";
            // Always push a new group with a fresh meta.key so React remounts
            // the FieldControl and picks up the restored values (Q1d fix).
            const newRangeGroup = FormBuilder.group({
              min: [minVal],
              max: [maxVal],
            });
            newRangeGroup.meta = { key: `relbio_range_${index}_${Date.now()}` };
            if (!wizardForm.contains(estimateRangeControlName)) {
              wizardForm.addControl(
                estimateRangeControlName,
                new FormArray([newRangeGroup])
              );
            } else {
              const arr = wizardForm.get(estimateRangeControlName);
              while (arr.length > 0) {
                arr.removeAt(0);
              }
              arr.push(newRangeGroup);
            }
          }
        });

        // Trigger re-render so BioavailabilityQuestions sees the restored blocks
        setRestoreVersion(v => v + 1);
      }
    }
  };

  const handleSubmit = createUnifiedSubmitHandler({
    wizardForm,
    qForm,
    studyId,
    sections: [WIZARD_SECTIONS.bioavailability],
    setAjaxLoading,
    setQuestionsDataSaved,
    questionsDataSaved,
    setFormFilledData,
    setWizardFormFilledData,
    setErrMsg,
    errRef,
    setIsSaving,
    questionsRef: null,
    CsrAPI,
    toast,
    isProduction,
    Sentry,
  });

  const PageContent = () => {
    return (
      <div className="questions-page">
        <span ref={errRef} className={errMsg ? "errmsg" : "offscreen"}>
          {errMsg}
        </span>
        <div className="questions-content">
          <div className="questions-content__items">
            {questionsData.current && (
              <FieldGroup
                control={wizardForm}
                render={() => (
                  <BioavailabilityQuestions
                    globalId={0}
                    wizardForm={wizardForm}
                    questionsData={questionsData.current}
                  />
                )}
              />
            )}
          </div>
        </div>
        <ToastContainer
          position="top-right"
          autoClose={2000}
          hideProgressBar={false}
          newestOnTop={false}
          closeOnClick={true}
          rtl={false}
          pauseOnFocusLoss={false}
          draggable
          pauseOnHover
          theme="light"
        />
      </div>
    );
  };

  return (
    <DashboardContent
      sidebarCSR={true}
      headerTitle={"Answer the following questions related to Bioavailability"}
      headerButton={false}
      pageContent={PageContent()}
      prevButton={{
        buttonTitle: "Comparative PK",
        buttonGoToAction: "/csr/" + studyId + "/comppharmacokinetics",
      }}
      nextButton={{
        buttonTitle: "Standard PD",
        buttonGoToAction: "/csr/" + studyId + "/stdpharmacodynamics",
      }}
      saveButtonHandler={handleSubmit}
      savePermission={PERMISSIONS.csrWizardsUpdate}
      saveButton={true}
    />
  );
};

export default Bioavailability;
